import React, { useMemo, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Row, Col } from 'reactstrap';
import { Card, Space, Spin, Tag, message } from 'antd';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getEvaluation, updateEvaluation, createEvaluation, reset } from './evaluation.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getClasse, getClasses } from 'app/entities/classe/classe.reducer';
import { getMatieresByClasseId } from 'app/entities/matiere-ecole/matiere-ecole.reducer';
import { getPeriodes, reset as resetPeriodes } from 'app/entities/periode-evaluation/periode-evaluation.reducer';
import { getAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { getSousMatieres, reset as resetSousMatieres } from 'app/entities/sous-matiere/sous-matiere.reducer';
import { EvaluationType } from 'app/shared/model/evaluation.model';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';

export const EvaluationUpdate = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string; id: string }>) => {
  const dispatch = useAppDispatch();
  const [isNew] = useState(!props.match.params || !props.match.params.id);

  const evaluationEntity = useAppSelector(state => state.evaluation.entity);
  const loading = useAppSelector(state => state.evaluation.loading);
  const updating = useAppSelector(state => state.evaluation.updating);
  const updateSuccess = useAppSelector(state => state.evaluation.updateSuccess);
  const classes = useAppSelector(state => state.classe.entities);
  const classeEntity = useAppSelector(state => state.classe.entity);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const sousMatieres = useAppSelector(state => state.sousMatiere.entities);
  const periodes = useAppSelector(state => state.periodeEvaluation.entities);
  const anneeScolaire = useAppSelector(state => state.anneeScolaire.entity);

  const { ecoleid, anneeScolaireId, id } = props.match.params;
  const classeIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(props.location.search || '');
    return params.get('classeId');
  }, [props.location.search]);
  const [selectedClasseId, setSelectedClasseId] = useState<string | null>(null);
  const [selectedPeriodeId, setSelectedPeriodeId] = useState<string | null>(null);
  const [selectedMatiereId, setSelectedMatiereId] = useState<string | null>(null);
  const selectedPeriode = useMemo(() => periodes.find(p => p.id === selectedPeriodeId), [periodes, selectedPeriodeId]);
  const currentPeriode = useMemo(() => periodes.find(periode => periode.statut === StatutPeriodeEvaluation.OUVERTE) || null, [periodes]);
  const selectedMatiere = useMemo(() => matieres.find(matiere => matiere.id === selectedMatiereId), [matieres, selectedMatiereId]);
  const selectedMatiereAvecSousMatieres = Boolean(selectedMatiere?.avecSousMatieres);
  const isSelectedPeriodeOpen = selectedPeriode ? selectedPeriode.statut === StatutPeriodeEvaluation.OUVERTE : true;
  const canCreateEvaluation = !isNew || Boolean(currentPeriode?.id);
  const dateConstrainedPeriode = isNew ? currentPeriode : selectedPeriode;

  const handleClose = () => {
    if (classeIdFromQuery) {
      props.history.push(`/calendar/${ecoleid}/${anneeScolaireId}/${classeIdFromQuery}/calendarEntry?tab=3`);
      return;
    }
    props.history.push(`/evaluation/${ecoleid}/${anneeScolaireId}`);
  };

  useEffect(() => {
    dispatch(getClasses(ecoleid));
    dispatch(getAnneeScolaire(anneeScolaireId));
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(reset());
      dispatch(getEvaluation(id));
    }
  }, [ecoleid, id, isNew]);

  useEffect(() => {
    if (isNew && classeIdFromQuery) {
      setSelectedClasseId(classeIdFromQuery);
      return;
    }
    if (!isNew && evaluationEntity?.id !== id) return;
    if (evaluationEntity?.classeId) {
      setSelectedClasseId(evaluationEntity.classeId);
    }
    if (evaluationEntity?.periodeId) {
      setSelectedPeriodeId(evaluationEntity.periodeId);
    }
    if (evaluationEntity?.matiereId) {
      setSelectedMatiereId(evaluationEntity.matiereId);
    }
  }, [
    evaluationEntity?.id,
    evaluationEntity?.classeId,
    evaluationEntity?.periodeId,
    evaluationEntity?.matiereId,
    id,
    isNew,
    classeIdFromQuery,
  ]);

  useEffect(() => {
    if (selectedClasseId) {
      dispatch(resetPeriodes());
      dispatch(getClasse(selectedClasseId));
      dispatch(getMatieresByClasseId(selectedClasseId));
    } else {
      dispatch(resetPeriodes());
      setSelectedPeriodeId(null);
    }
  }, [dispatch, selectedClasseId]);

  useEffect(() => {
    if (classeEntity?.id === selectedClasseId && classeEntity?.cycleId && anneeScolaireId) {
      dispatch(getPeriodes({ ecoleId: ecoleid, anneeScolaireId, cycleId: classeEntity.cycleId }));
    }
  }, [classeEntity?.id, classeEntity?.cycleId, selectedClasseId, anneeScolaireId]);

  useEffect(() => {
    if (classeEntity?.id === selectedClasseId && classeEntity?.cycleId && selectedMatiereId && selectedMatiereAvecSousMatieres) {
      dispatch(
        getSousMatieres({
          cycleMatiereId: selectedMatiere?.cycleMatiereId,
          cycleId: classeEntity.cycleId,
          matiereEcoleId: selectedMatiereId,
        })
      );
    } else {
      dispatch(resetSousMatieres());
    }
  }, [
    dispatch,
    classeEntity?.id,
    classeEntity?.cycleId,
    selectedClasseId,
    selectedMatiereId,
    selectedMatiere?.cycleMatiereId,
    selectedMatiereAvecSousMatieres,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (selectedPeriodeId && periodes.length > 0 && !periodes.find(p => p.id === selectedPeriodeId)) {
      setSelectedPeriodeId(null);
    }
  }, [periodes, selectedPeriodeId]);

  useEffect(() => {
    if (!isNew) {
      return;
    }
    setSelectedPeriodeId(currentPeriode?.id || null);
  }, [isNew, currentPeriode?.id]);

  const formatDateLabel = (value?: string | null) => {
    if (!value) return 'Date non définie';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
  };

  const isDateWithinPeriode = (date?: string | null, periode = dateConstrainedPeriode) => {
    if (!date || !periode?.dateDebut || !periode?.dateFin) {
      return true;
    }
    return date >= periode.dateDebut && date <= periode.dateFin;
  };

  const saveEntity = values => {
    const periodeId = isNew ? currentPeriode?.id : values.periodeId;
    if (!periodeId) {
      message.error('La période est requise');
      return;
    }
    const periodeToCheck = periodes.find(p => p.id === periodeId);
    if (!periodeToCheck) {
      message.error('Période introuvable');
      return;
    }
    if (isNew && periodeToCheck.statut !== StatutPeriodeEvaluation.OUVERTE) {
      message.error('La période doit être ouverte pour créer une évaluation');
      return;
    }
    if (!values.date) {
      message.error("La date de l'évaluation est requise");
      return;
    }
    if (!isDateWithinPeriode(values.date, periodeToCheck)) {
      message.error(
        `La date doit être comprise entre le ${formatDateLabel(periodeToCheck.dateDebut)} et le ${formatDateLabel(periodeToCheck.dateFin)}`
      );
      return;
    }
    const definedValues = Object.keys(values).reduce<Record<string, any>>((acc, key) => {
      if (values[key] !== undefined) {
        acc[key] = values[key];
      }
      return acc;
    }, {});
    if (!selectedMatiereAvecSousMatieres) {
      definedValues.sousMatiereId = null;
    } else if (!sousMatieres.some(sousMatiere => sousMatiere.id === definedValues.sousMatiereId && sousMatiere.actif !== false)) {
      message.error('Une sous-matière active est requise pour cette matière');
      return;
    }
    const entity = {
      ...(!isNew && evaluationEntity),
      ...definedValues,
      ...(isNew && classeIdFromQuery ? { classeId: classeIdFromQuery } : {}),
      periodeId,
      ecoleId: ecoleid,
      anneeScolaireId,
    };
    if (isNew) {
      dispatch(createEvaluation(entity));
    } else {
      dispatch(updateEvaluation(entity));
    }
  };

  const defaultValues = useMemo(
    () =>
      isNew
        ? {
            classeId: classeIdFromQuery || undefined,
          }
        : {
            ...evaluationEntity,
          },
    [isNew, evaluationEntity, classeIdFromQuery]
  );

  const isEntityLoaded = isNew || evaluationEntity?.id === id;
  const isFormReady = !loading && isEntityLoaded;

  const formContent = (
    <Spin spinning={!isFormReady}>
      {isFormReady ? (
        <ValidatedForm
          key={isNew ? 'new' : `loaded-${id}-${classes.length}-${periodes.length}-${matieres.length}`}
          defaultValues={defaultValues}
          onSubmit={saveEntity}
        >
          {isNew && selectedClasseId && (
            <div
              style={{
                marginBottom: 20,
                borderRadius: 18,
                overflow: 'hidden',
                border: currentPeriode ? '1px solid rgba(15, 118, 110, 0.18)' : '1px solid rgba(180, 83, 9, 0.18)',
                boxShadow: currentPeriode ? '0 18px 40px rgba(15, 118, 110, 0.10)' : '0 18px 40px rgba(180, 83, 9, 0.10)',
                background: currentPeriode
                  ? 'linear-gradient(135deg, #f0fdf4 0%, #ecfeff 52%, #eff6ff 100%)'
                  : 'linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)',
              }}
            >
              <div
                style={{
                  padding: '18px 20px 16px',
                  background: currentPeriode
                    ? 'linear-gradient(90deg, rgba(15,118,110,0.10) 0%, rgba(2,132,199,0.08) 100%)'
                    : 'linear-gradient(90deg, rgba(234,88,12,0.10) 0%, rgba(217,119,6,0.08) 100%)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: currentPeriode ? '#0f766e' : '#b45309',
                        fontWeight: 700,
                        marginBottom: 6,
                      }}
                    >
                      Période
                    </div>
                    <div
                      style={{
                        fontSize: 22,
                        lineHeight: 1.2,
                        fontWeight: 800,
                        color: '#0f172a',
                      }}
                    >
                      {currentPeriode ? currentPeriode.nom || 'Période ouverte' : 'Aucune période ouverte'}
                    </div>
                  </div>
                  <Tag
                    color={currentPeriode ? 'green' : 'orange'}
                    style={{ borderRadius: 999, paddingInline: 12, fontWeight: 700, marginRight: 0 }}
                  >
                    {currentPeriode ? 'En cours' : ' - '}
                  </Tag>
                </div>
              </div>
              <div style={{ padding: '18px 20px 20px' }}>
                {currentPeriode ? (
                  <>
                    <div style={{ color: '#334155', fontSize: 14, marginBottom: 14 }}>
                      Toute nouvelle évaluation sera automatiquement rattachée à la période en cours.
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <div
                        style={{
                          minWidth: 180,
                          background: 'rgba(255,255,255,0.72)',
                          border: '1px solid rgba(148,163,184,0.20)',
                          borderRadius: 14,
                          padding: '12px 14px',
                        }}
                      >
                        <div
                          style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 4 }}
                        >
                          Début
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{formatDateLabel(currentPeriode.dateDebut)}</div>
                      </div>
                      <div
                        style={{
                          minWidth: 180,
                          background: 'rgba(255,255,255,0.72)',
                          border: '1px solid rgba(148,163,184,0.20)',
                          borderRadius: 14,
                          padding: '12px 14px',
                        }}
                      >
                        <div
                          style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 4 }}
                        >
                          Fin
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{formatDateLabel(currentPeriode.dateFin)}</div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 220,
                          background: 'rgba(15,118,110,0.08)',
                          border: '1px solid rgba(15,118,110,0.14)',
                          borderRadius: 14,
                          padding: '12px 14px',
                          color: '#134e4a',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        La date saisie sera acceptée uniquement entre le {formatDateLabel(currentPeriode.dateDebut)} et le{' '}
                        {formatDateLabel(currentPeriode.dateFin)}.
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#7c2d12', fontSize: 14, fontWeight: 600 }}>
                    Vous devez ouvrir une période d&apos;évaluation avant de pouvoir créer une nouvelle évaluation.
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedPeriode && selectedPeriode.statut !== StatutPeriodeEvaluation.OUVERTE && (
            <div style={{ marginBottom: 12 }}>
              <Tag color={selectedPeriode.statut === StatutPeriodeEvaluation.FERMEE ? 'red' : 'blue'}>
                {selectedPeriode.statut === StatutPeriodeEvaluation.FERMEE ? 'Période fermée' : 'Période planifiée'}
              </Tag>
            </div>
          )}
          {!isNew ? (
            <ValidatedField
              name="id"
              required
              readOnly
              id="evaluation-id"
              label={translate('global.field.id')}
              validate={{ required: true }}
            />
          ) : null}

          <ValidatedField
            label={translate('korischoolApp.evaluation.classe')}
            id="evaluation-classeId"
            name="classeId"
            data-cy="classeId"
            type="select"
            disabled={Boolean(classeIdFromQuery) || !isNew}
            onChange={e => {
              setSelectedClasseId(e.target.value || null);
              setSelectedPeriodeId(null);
            }}
          >
            <option value="" key="0" />
            {classes.map(classe => (
              <option value={classe.id} key={classe.id}>
                {classe.nom}
              </option>
            ))}
          </ValidatedField>
          <ValidatedField
            label={translate('korischoolApp.evaluation.type')}
            id="evaluation-type"
            name="type"
            data-cy="type"
            type="select"
            validate={{ required: { value: true, message: 'Le type est requis' } }}
          >
            <option value="" key="0" />
            <option value={EvaluationType.DEVOIR}>{EvaluationType.DEVOIR}</option>
            <option value={EvaluationType.COMPOSITION}>{EvaluationType.COMPOSITION}</option>
            <option value={EvaluationType.CONTROLE}>{EvaluationType.CONTROLE}</option>
          </ValidatedField>
          <ValidatedField
            label="Titre"
            id="evaluation-titre"
            name="titre"
            data-cy="titre"
            type="text"
            validate={{ required: { value: true, message: 'Le titre est requis' } }}
          />
          <ValidatedField
            label={translate('korischoolApp.evaluation.date')}
            id="evaluation-date"
            name="date"
            data-cy="date"
            type="date"
            min={dateConstrainedPeriode?.dateDebut || undefined}
            max={dateConstrainedPeriode?.dateFin || undefined}
            validate={{
              required: { value: true, message: "La date de l'évaluation est requise" },
              validate: value =>
                isDateWithinPeriode(value) ||
                `La date doit être comprise entre le ${formatDateLabel(dateConstrainedPeriode?.dateDebut)} et le ${formatDateLabel(
                  dateConstrainedPeriode?.dateFin
                )}`,
            }}
          />
          <ValidatedField
            label={translate('korischoolApp.evaluation.bareme')}
            id="evaluation-bareme"
            name="bareme"
            data-cy="bareme"
            type="number"
            validate={{ required: { value: isNew, message: 'Le barème est requis' } }}
          />
          {!isNew && (
            <ValidatedField
              label={translate('korischoolApp.evaluation.periode')}
              id="evaluation-periodeId"
              name="periodeId"
              data-cy="periodeId"
              type="select"
              onChange={e => setSelectedPeriodeId(e.target.value || null)}
            >
              <option value="" key="0" />
              {periodes.map(periode => (
                <option value={periode.id} key={periode.id}>
                  {periode.nom}
                </option>
              ))}
            </ValidatedField>
          )}
          <ValidatedField
            label={translate('korischoolApp.evaluation.matiere')}
            id="evaluation-matiereId"
            name="matiereId"
            data-cy="matiereId"
            type="select"
            onChange={e => setSelectedMatiereId(e.target.value || null)}
            validate={{ required: { value: true, message: 'La matière est requise' } }}
          >
            <option value="" key="0" />
            {matieres.map(matiere => (
              <option value={matiere.id} key={matiere.id}>
                {matiere.nom}
              </option>
            ))}
          </ValidatedField>
          {selectedMatiereAvecSousMatieres && (
            <ValidatedField
              label="Sous-matière"
              id="evaluation-sousMatiereId"
              name="sousMatiereId"
              data-cy="sousMatiereId"
              type="select"
              validate={{ required: { value: true, message: 'La sous-matière est requise' } }}
            >
              <option value="" key="0" />
              {sousMatieres
                .filter(sousMatiere => sousMatiere.actif !== false)
                .map(sousMatiere => (
                  <option value={sousMatiere.id} key={sousMatiere.id}>
                    {sousMatiere.nom}
                  </option>
                ))}
            </ValidatedField>
          )}

          <Space>
            <Button onClick={handleClose} id="cancel-save" data-cy="entityCreateCancelButton" color="info">
              <FontAwesomeIcon icon="arrow-left" />
              &nbsp;
              <span className="d-none d-md-inline">
                <Translate contentKey="entity.action.back">Back</Translate>
              </span>
            </Button>
            <Button
              style={{ backgroundColor: '#F28E19', color: 'white' }}
              id="save-entity"
              data-cy="entityCreateSaveButton"
              type="submit"
              disabled={updating || (isNew && (!isSelectedPeriodeOpen || !canCreateEvaluation))}
            >
              <FontAwesomeIcon icon="save" />
              &nbsp;
              <Translate contentKey="entity.action.save">Save</Translate>
            </Button>
          </Space>
        </ValidatedForm>
      ) : (
        <div style={{ minHeight: 200 }} />
      )}
    </Spin>
  );

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="korischoolApp.evaluation.home.createOrEditLabel" data-cy="EvaluationCreateUpdateHeading">
            <Translate contentKey="korischoolApp.evaluation.home.createOrEditLabel">Create or edit an Evaluation</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          <Card style={{ marginTop: 12 }}>{formContent}</Card>
        </Col>
      </Row>
    </div>
  );
};

export default EvaluationUpdate;
